/* ==========================================================================
   EL BENDECIDO — INTERNAL UI EVENT BUS (emitter)
   ==========================================================================
   Emits the DOM CustomEvent `elb:analytics`. This is the theme's own event
   bus; it talks to nothing external. assets/elb-analytics-bridge.js listens
   and decides what, if anything, reaches Shopify Customer Events.

   WHY DELEGATION
   The previous version bound a listener to each [data-elb-event] node inside
   DOMContentLoaded. That had two failure modes:
     - a section re-rendered by the Theme Editor lost its listeners entirely,
       because DOMContentLoaded never fires again;
     - if the script were ever loaded twice, every control would emit twice.
   Listeners are now delegated to `document` and guarded, so re-rendered
   sections keep working and double-binding is impossible.

   No customer-entered value, mailto target, map URL or free text is ever
   placed in an event payload.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__elbEventBusReady) return;
  window.__elbEventBusReady = true;

  function emit(name, detail) {
    var payload = {
      event: name,
      path: window.location.pathname,
      language: document.documentElement.lang || 'en'
    };
    if (detail) {
      for (var k in detail) {
        if (Object.prototype.hasOwnProperty.call(detail, k)) payload[k] = detail[k];
      }
    }
    document.dispatchEvent(new CustomEvent('elb:analytics', { detail: payload }));
  }

  /* ---------- clicks: estimate / email / directions / phone ---------- */
  document.addEventListener(
    'click',
    function (event) {
      var control =
        event.target && event.target.closest ? event.target.closest('[data-elb-event]') : null;
      if (!control) return;
      if (control.getAttribute('aria-disabled') === 'true') return;

      // `placement` stays on the DOM bus for backward compatibility.
      // `action_location` is the analytics-facing value the bridge sends.
      emit(control.getAttribute('data-elb-event'), {
        placement: control.getAttribute('data-elb-placement') || 'unspecified',
        action_location: control.getAttribute('data-elb-location') || null
      });
    },
    true // capture: runs before navigation begins
  );

  /* ---------- language switch ---------- */
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches('[data-elb-language-selector]')) return;

    emit('language_switch', {
      from_language: document.documentElement.lang || 'en',
      to_language: select.value,
      placement: 'header',
      action_location: 'header'
    });

    // Dispatch is synchronous, so the event is already out before the form
    // submits. Switching is not delayed.
    if (select.form) select.form.submit();
  });

  /* ---------- estimate form (internal-only events) ----------
     estimate_start and estimate_preview_complete are intentionally NOT in the
     bridge allowlist. They are useful for in-page behaviour but are not
     published to Shopify Customer Events. */
  var formStarted = false;

  document.addEventListener('input', function (event) {
    var form =
      event.target && event.target.closest
        ? event.target.closest('[data-elb-estimate-form]')
        : null;
    if (!form || formStarted) return;
    formStarted = true;
    emit('estimate_start', { placement: 'contact_form' });
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('[data-elb-estimate-form]')) return;
    event.preventDefault();
    if (!form.reportValidity()) return;
    var status = form.querySelector('[data-elb-form-status]');
    if (status) {
      status.hidden = false;
      status.focus();
    }
    emit('estimate_preview_complete', { placement: 'contact_form' });
  });

  /* ---------- service_view: once per service page ----------
     The key comes from a Liquid-rendered data attribute driven by page.handle,
     never from page text. The bridge additionally validates it against its own
     allowlist. */
  var servicesSeen = {};

  function emitServiceView() {
    var node = document.querySelector('[data-elb-service]');
    if (!node) return;
    var key = node.getAttribute('data-elb-service');
    if (!key || servicesSeen[key]) return;
    servicesSeen[key] = true;
    emit('service_view', { service_key: key });
  }

  /* ---------- pricing_view: once when the component is meaningfully visible ---------- */
  var pricingSeen = false;

  function selectedVehicle(root) {
    var checked = root.querySelector('.elb-segment__input:checked');
    return checked ? checked.value : null;
  }

  function emitPricingView(root) {
    if (pricingSeen) return;
    pricingSeen = true;
    var detail = { component_name: 'pricing_selector', action_location: 'pricing_selector' };
    var v = selectedVehicle(root);
    if (v) detail.vehicle_class = v;
    emit('pricing_view', detail);
  }

  function watchPricing() {
    var root = document.querySelector('[data-elb-pricing]');
    if (!root || pricingSeen) return;

    if (typeof window.IntersectionObserver !== 'function') {
      // Fallback: no observer support. Treat presence in the document as a
      // view rather than dropping the event or polling on scroll.
      emitPricingView(root);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            emitPricingView(root);
            observer.disconnect(); // once, never repeatedly while scrolling
            return;
          }
        }
      },
      { threshold: 0.35 } // "meaningfully visible", not a one-pixel sliver
    );
    observer.observe(root);
  }

  function boot() {
    emitServiceView();
    watchPricing();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme Editor re-renders a section without a page load. Re-running boot()
  // is safe: both emitters are idempotent via servicesSeen / pricingSeen.
  document.addEventListener('shopify:section:load', boot);
})();
